import { useEffect, useRef, useState } from 'react';
import { get, post, qs } from '../lib/api';
import { navigate, useDebounced, useIdle, useRoute, useScanner } from '../lib/hooks';
import { useSession } from '../lib/session';
import { CartProvider, useCart } from '../lib/cart';
import { Button, IconButton, Sheet, useToast } from '../ui/kit';
import { EmployeeBadge } from '../ui/brand';
import { Icon, type IconName } from '../ui/icons';
import { NavLegal } from '../ui/legal';
import { UpdateBar } from '../lib/update';
import { HomeScreen, ComingSoonScreen } from './Home';
import { CartSheet, SaleScreen, useCartTotals } from './Sale';
import { formatCents } from '../../shared/money';
import { SalesScreen, SaleDetailScreen } from './Sales';
import { InventoryScreen } from './Inventory';
import { CustomersScreen, CustomerProfileScreen } from './Customers';
import { DrawerScreen } from './Drawer';
import { EmployeesScreen } from './Employees';
import { SettingsScreen } from './Settings';
import { RepairCheckInScreen, RepairDetailScreen, RepairsScreen } from './Repairs';
import { BuyScreen, BuybackDetailScreen, BuybacksScreen } from './Buyback';
import { BulkBuyScreen, BuyManyScreen, BuybackBatchScreen } from './BuyMany';
import { EasyBuyScreen } from './EasyBuy';
import { ActivationScreen, BillPayScreen } from './Carrier';
import { OrderDetailScreen, OrderNewScreen, OrdersScreen, startOrderWithPart } from './Orders';
import { SupplierPartHits } from './PartSearch';
import { ExpensesScreen, ReportsScreen } from './Reports';
import { PhoneCheckListRow, PhoneCheckNavItem, PhoneCheckProvider } from './PhoneCheck';
import { MessagesScreen, useMessagesBadge } from './Messages';
import { CustomerPicker } from './CustomerPicker';
import { GoalsBar } from './Goals';
import { UnlocksScreen } from './Unlocks';
import { usePlan } from '../lib/plan';
import { GettingStartedScreen } from './GettingStarted';
import { JobCheckInScreen, useJobKind } from './Jobs';

interface NavItem { to: string; label: string; icon: IconName; match: string[]; show?: boolean; count?: number }

export function Shell() {
  const { me, can, lock, clockOut, refresh } = useSession();
  const toast = useToast();
  const endShift = async () => {
    const h = await clockOut();
    if (h !== null) toast(`Clocked out. ${h.toFixed(2)} hours on this shift.`, 'good');
  };
  const route = useRoute();
  const [myBadge, setMyBadge] = useState(false);
  // "Add customer" in the top bar, on every screen.
  const [addingCustomer, setAddingCustomer] = useState(false);
  // The cart, from every screen (New sale shows it beside the menu).
  const [cartOpen, setCartOpen] = useState(false);
  const plan = usePlan();
  // Texting and calls run on West Coast Wireless's own Twilio number: its store only, for now.
  const texting = can('messages.use') && plan.home;
  const unreadTexts = useMessagesBadge(texting);
  const idleMinutes = Number(me?.settings.idle_lock_minutes ?? 5);
  useIdle(idleMinutes, () => { if (me?.session_kind === 'pin') lock(); }, me?.session_kind === 'pin');

  const nav: NavItem[] = [
    { to: '/', label: 'Today', icon: 'home', match: [''] },
    // Sales opens a new sale with the menu (Apple, Samsung, Google, Moto, WiFi, Accessories); history is a tap away.
    { to: '/sale', label: 'Sales', icon: 'bag', match: ['sale', 'sales'] },
    { to: '/repairs', label: plan.industry === 'auto' ? 'Work orders' : 'Repairs', icon: 'wrench', match: ['repairs'] },
    { to: '/inventory', label: 'Inventory', icon: 'boxes', match: ['inventory'] },
    { to: '/customers', label: 'Customers', icon: 'users', match: ['customers'] },
    { to: '/messages', label: 'Messages', icon: 'chat', match: ['messages'], show: texting, count: unreadTexts },
    { to: '/reports', label: 'Reports', icon: 'chart', match: ['reports'] },
  ];
  const extra: NavItem[] = [
    { to: '/drawer', label: 'Cash drawer', icon: 'cash', match: ['drawer'], show: !!me?.register || can('drawer.review') },
    { to: '/employees', label: 'Employees', icon: 'badge', match: ['employees'], show: can('employees.manage') },
    // Carrier unlocks on the owner's UnlockBase account: the owner alone sees it (the server refuses everyone else).
    { to: '/unlocks', label: 'Unlocks', icon: 'unlock', match: ['unlocks'], show: me?.employee.role_id === 'owner' && plan.industry === 'cellphone' },
    { to: '/settings', label: 'Settings', icon: 'sliders', match: ['settings'], show: can('settings.manage') || can('registers.manage') || can('audit.view') },
  ];
  const first = route.parts[0] ?? '';
  const current = (n: NavItem) => n.match.includes(first);

  // A barcode scanned anywhere (with no text box in use) opens what it belongs to: a receipt, a
  // repair ticket, a special order or a phone bought. The sale screen and forms handle their own scans.
  const second = route.parts[1] ?? '';
  const formOpen = ['sale', 'buy', 'trade', 'activation', 'billpay'].includes(first) || (['repairs', 'orders'].includes(first) && second === 'new');
  useScanner((code) => {
    get<Hit[]>(`/api/search${qs({ q: code.trim() })}`)
      .then((hits) => { if (hits[0]) navigate(hitPath(hits[0])); else toast(`Nothing matches “${code.trim()}”.`, 'error'); })
      .catch((e) => toast(e instanceof Error ? e.message : 'Search failed.', 'error'));
  }, !!me && !formOpen && !cartOpen);
  // Going to another screen (Add a trade-in, a scan that opens a record) closes the cart.
  useEffect(() => { setCartOpen(false); }, [route.path]);

  const switchStore = async (id: string) => {
    await post('/api/auth/location', { locationId: id });
    await refresh();
  };

  return (
    <CartProvider scope={me?.register?.id ?? 'office'}>
      <PhoneCheckProvider>
      <div className="app">
        <ShellUpdateBar />
        <nav className="nav" aria-label="Main">
          <a className="nav-brand" href="#/" aria-label="STORE OS home">
            <img src="/assets/store-os-icon-128.png" alt="" width={44} height={44} />
            <span>STORE OS</span>
          </a>
          {nav.filter((n) => n.show !== false).map((n) => <NavLink key={n.to} item={n} current={current(n)} />)}
          <PhoneCheckNavItem />
          <div className="nav-spacer" />
          {extra.filter((n) => n.show).map((n) => <NavLink key={n.to} item={n} current={current(n)} />)}
          <button type="button" className="nav-item" onClick={() => lock()} title="Switch person. You stay clocked in."><NavIcon icon="lock" /><span>Lock</span></button>
          {me?.session_kind === 'pin' ? <button type="button" className="nav-item" onClick={endShift}><NavIcon icon="clock" /><span>Clock out</span></button> : null}
          <NavLegal />
        </nav>

        <header className="top">
          <div className="top-store">
            <span className="biz">{me?.settings.business_name}</span>
            {me?.register || (me?.locations.length ?? 0) < 2 ? (
              <span className="loc">{me?.location?.name}{me?.register ? ` · ${me.register.name}` : ''}</span>
            ) : (
              <select className="select" style={{ minHeight: 36, padding: '4px 8px', fontWeight: 700 }} value={me?.location?.id ?? ''} onChange={(e) => switchStore(e.target.value)} aria-label="Store">
                {me?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
          </div>
          <div className="top-search"><GlobalSearch /></div>
          {can('sale.create') && first !== 'sale' ? <CartButton onOpen={() => setCartOpen(true)} /> : null}
          {can('customer.edit') ? (
            <Button className="top-add" variant="primary" icon="plus" onClick={() => setAddingCustomer(true)} title="Add a customer" aria-label="Add customer">
              <span className="top-add-t">Add customer</span>
            </Button>
          ) : null}
          <div className="top-user">
            {me ? (
              <button type="button" className="top-badge" onClick={() => setMyBadge(true)} aria-label={`${me.employee.first_name}'s badge`} title="My badge">
                <EmployeeBadge person={me.employee} />
              </button>
            ) : null}
            <span className="who">
              <span className="strong" style={{ display: 'block', lineHeight: '20px' }}>{me?.employee.first_name}</span>
              <span className="tiny muted" style={{ display: 'block' }}>{me?.employee.role}</span>
            </span>
            {me?.session_kind === 'pin' ? <IconButton icon="clock" label="Clock out (end my shift)" onClick={endShift} /> : null}
            <IconButton icon="lock" label="Lock (switch person, stay clocked in)" onClick={() => lock()} />
          </div>
        </header>

        <main className="main" id="main">
          <SubscriptionBanner />
          <Routes />
        </main>
        <GoalsBar />
        {addingCustomer ? (
          <CustomerPicker
            startAdding=""
            onClose={() => setAddingCustomer(false)}
            onPick={(c) => { setAddingCustomer(false); toast(`${c.name} is saved.`, 'good'); navigate(`/customers/${c.id}`); }}
          />
        ) : null}
        {cartOpen ? <CartSheet onClose={() => setCartOpen(false)} /> : null}
        {myBadge && me ? (
          <Sheet title={me.employee.name} onClose={() => setMyBadge(false)} footer={<Button variant="primary" onClick={() => setMyBadge(false)}>Done</Button>}>
            <div className="badge-view">
              <EmployeeBadge person={me.employee} className="big" />
              <p className="small muted">{me.employee.role}{me.location ? ` · ${me.location.name}` : ''}</p>
            </div>
          </Sheet>
        ) : null}

        <nav className="bottom-nav" aria-label="Main">
          <NavLink item={{ to: '/', label: 'Today', icon: 'home', match: [''] }} current={first === ''} />
          <NavLink item={{ to: '/sale', label: 'Sell', icon: 'bag', match: ['sale'] }} current={first === 'sale'} />
          <NavLink item={{ to: '/inventory', label: 'Inventory', icon: 'boxes', match: ['inventory'] }} current={first === 'inventory'} />
          <NavLink item={{ to: '/customers', label: 'Customers', icon: 'users', match: ['customers'] }} current={first === 'customers'} />
          <NavLink item={{ to: '/more', label: 'More', icon: 'list', match: ['more'], count: unreadTexts }} current={first === 'more'} />
        </nav>
      </div>
      </PhoneCheckProvider>
    </CartProvider>
  );
}

/** The Cart button in the top bar: how many items and the total; opens the cart. */
function CartButton({ onOpen }: { onOpen: () => void }) {
  const { cart } = useCart();
  const { totals } = useCartTotals();
  const count = cart.lines.reduce((n, l) => n + (l.kind === 'product' ? l.qty : 1), 0);
  const label = count ? `Cart: ${count} item${count === 1 ? '' : 's'}, ${formatCents(totals.total)}` : 'Cart (empty)';
  return (
    <button type="button" className={`top-cart${count ? ' has' : ''}`} onClick={onOpen} aria-label={label} title={label}>
      <Icon.bag />
      <span className="top-cart-t">{count ? formatCents(totals.total) : 'Cart'}</span>
      {count ? <span className="top-cart-n" aria-hidden="true">{count > 99 ? '99+' : count}</span> : null}
    </button>
  );
}

/** A newer STORE OS is live: refresh now, or by itself once the store is quiet (never with a sale in the cart). */
function ShellUpdateBar() {
  const { cart } = useCart();
  return <UpdateBar busy={cart.lines.length > 0 || (cart.tradeIns?.length ?? 0) > 0} />;
}

/** The colour of each menu item's tile: a colour a glance tells apart, the store's blue for the rest. */
const NAV_TONES: Partial<Record<IconName, string>> = {
  home: 'sunset', bag: 'green', wrench: 'amber', boxes: 'violet', users: 'teal', chat: 'sky', chart: 'blue',
  cash: 'emerald', badge: 'pink', unlock: 'indigo', sliders: 'slate', list: 'slate', lock: 'slate', clock: 'slate', search: 'navy',
};

/** A menu item's icon on its colour tile. */
export function NavIcon({ icon }: { icon: IconName }) {
  const I = Icon[icon];
  return <span className={`nav-ico nav-ico-${NAV_TONES[icon] ?? 'blue'}`}><I /></span>;
}

function NavLink({ item, current }: { item: NavItem; current: boolean }) {
  return (
    <a className="nav-item" href={`#${item.to}`} aria-current={current ? 'page' : undefined}>
      <NavIcon icon={item.icon} />
      <span>{item.label}</span>
      {item.count ? <span className="nav-count" aria-label={`${item.count} new`}>{item.count > 99 ? '99+' : item.count}</span> : null}
    </a>
  );
}

function Routes() {
  const r = useRoute();
  const [a, b] = r.parts;
  // A mechanic writes work orders; vacuum and computer shops check in their own items.
  const jobKind = useJobKind();
  useEffect(() => {
    document.getElementById('main')?.scrollTo(0, 0);
  }, [r.path]);
  switch (a ?? '') {
    case '':
      return <HomeScreen />;
    case 'sale':
      return <SaleScreen />;
    case 'sales':
      return b ? <SaleDetailScreen id={b} justSold={r.query.get('done') === '1'} /> : <SalesScreen />;
    case 'inventory':
      return <InventoryScreen tab={b} query={r.query} />;
    case 'customers':
      return b ? <CustomerProfileScreen id={b} /> : <CustomersScreen />;
    case 'drawer':
      return <DrawerScreen />;
    case 'employees':
      return <EmployeesScreen />;
    case 'unlocks':
      return <UnlocksScreen />;
    case 'settings':
      return <SettingsScreen tab={b} />;
    case 'billpay':
      return <BillPayScreen />;
    case 'activation':
      return <ActivationScreen />;
    case 'orders':
      return b === 'new' ? <OrderNewScreen key={r.query.get('part') ?? 'new'} query={r.query} /> : b ? <OrderDetailScreen key={b} id={b} query={r.query} /> : <OrdersScreen query={r.query} />;
    case 'expenses':
      return <ExpensesScreen />;
    case 'buy':
      return <BuyScreen trade={false} />;
    case 'trade':
      return <BuyScreen trade query={r.query} />;
    case 'buy-many':
      if (b === 'bulk') return <BulkBuyScreen />;
      if (b === 'easy') return <EasyBuyScreen />;
      return <BuyManyScreen key={r.query.get('checks') ?? ''} query={r.query} />;
    case 'buybacks':
      if (b === 'batch' && r.parts[2]) return <BuybackBatchScreen key={r.parts[2]} id={r.parts[2]} query={r.query} />;
      return b ? <BuybackDetailScreen id={b} query={r.query} /> : <BuybacksScreen />;
    case 'repairs':
      if (b === 'new') return jobKind ? <JobCheckInScreen kind={jobKind} query={r.query} /> : <RepairCheckInScreen query={r.query} />;
      return b ? <RepairDetailScreen id={b} query={r.query} /> : <RepairsScreen query={r.query} />;
    case 'reports':
      return <ReportsScreen tab={b} />;
    case 'more':
      return <MoreScreen />;
    case 'getting-started':
      return <GettingStartedScreen />;
    case 'messages':
      return <MessagesScreen id={b} query={r.query} />;
    default:
      return <ComingSoonScreen title="Not found" stage="future">That page doesn't exist. <a href="#/">Go to Today</a>.</ComingSoonScreen>;
  }
}

function MoreScreen() {
  const { can, me, lock, clockOut } = useSession();
  const plan = usePlan();
  const toast = useToast();
  const phones = plan.industry === 'cellphone';
  const items: { to: string; label: string; icon: IconName; show: boolean }[] = [
    { to: '/getting-started', label: 'Getting started', icon: 'check', show: plan.subscriber && can('settings.manage') },
    { to: '/messages', label: 'Messages & calls', icon: 'chat', show: can('messages.use') && plan.home },
    { to: '/sales', label: 'Sales history', icon: 'history', show: true },
    { to: '/repairs', label: plan.industry === 'auto' ? 'Work orders' : 'Repairs', icon: 'wrench', show: true },
    { to: '/buybacks', label: 'Phones bought & trade-ins', icon: 'phoneCash', show: can('buyback.create') && plan.has('buybacks') && phones },
    { to: '/orders', label: 'Special orders', icon: 'truck', show: can('order.create') && plan.has('special_orders') },
    { to: '/expenses', label: 'Expenses', icon: 'cash', show: can('expense.create') },
    { to: '/reports', label: 'Reports', icon: 'chart', show: can('reports.store') },
    { to: '/reports/pnl', label: 'Profit & loss', icon: 'chart', show: can('reports.store') && can('profit.view') && plan.has('pnl') },
    { to: '/reports/wholesale', label: 'Wholesale history', icon: 'truck', show: can('wholesale.sell') && plan.has('buybacks') && phones },
    { to: '/drawer', label: 'Cash drawer', icon: 'cash', show: !!me?.register || can('drawer.review') },
    { to: '/employees', label: 'Employees', icon: 'badge', show: can('employees.manage') },
    { to: '/unlocks', label: 'Carrier unlocks', icon: 'unlock', show: me?.employee.role_id === 'owner' && plan.industry === 'cellphone' },
    { to: '/settings', label: 'Settings', icon: 'sliders', show: can('settings.manage') || can('registers.manage') || can('audit.view') },
  ];
  return (
    <div className="page">
      <h1>More</h1>
      <div className="list">
        {items.filter((i) => i.show).map((i) => {
          const I = Icon[i.icon];
          return (
            <a key={i.to} className="list-row" href={`#${i.to}`}>
              <I />
              <span className="main-col t">{i.label}</span>
              <Icon.chevronRight />
            </a>
          );
        })}
        <PhoneCheckListRow />
      </div>
      <div className="row-wrap">
        <Button icon="lock" onClick={() => lock()}>Lock (switch person)</Button>
        {me?.session_kind === 'pin' ? <Button icon="clock" onClick={async () => { const h = await clockOut(); if (h !== null) toast(`Clocked out. ${h.toFixed(2)} hours on this shift.`, 'good'); }}>Clock out</Button> : null}
      </div>
    </div>
  );
}

interface Hit { type: string; id: string; title: string; subtitle: string; badge?: string }

/** Where a search hit (or a scanned receipt, ticket or label) opens. */
function hitPath(h: Hit): string {
  const to: Record<string, string> = {
    customer: `/customers/${h.id}`,
    device: `/inventory/devices?id=${h.id}`,
    product: `/inventory/products?id=${h.id}`,
    sale: `/sales/${h.id}`,
    transfer: '/inventory/transfers',
    repair: `/repairs/${h.id}`,
    buyback: `/buybacks/${h.id}`,
    order: `/orders/${h.id}`,
  };
  return to[h.type] ?? '/';
}

/** The one search bar: customers, phones (IMEI/serial), products (SKU/barcode), receipts and transfers. */
function GlobalSearch() {
  const toast = useToast();
  const { can } = useSession();
  const plan = usePlan();
  const searchHint = plan.industry === 'auto' ? 'Search customers, vehicles, VIN, receipts, work orders, SKUs'
    : plan.industry === 'cellphone' ? 'Search customers, phones, IMEI, receipts, repairs, SKUs'
    : `Search customers, ${plan.industry === 'computer' ? 'computers' : 'vacuums'}, serial numbers, receipts, repairs, SKUs`;
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 220);
  const [hits, setHits] = useState<Hit[]>([]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    if (dq.trim().length < 2) {
      setHits([]);
      return;
    }
    get<Hit[]>(`/api/search${qs({ q: dq })}`)
      .then((h) => { if (live) { setHits(h); setSel(0); } })
      .catch((e) => toast(e.message, 'error'));
    return () => { live = false; };
  }, [dq, toast]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const go = (h: Hit) => {
    setOpen(false);
    setQ('');
    navigate(hitPath(h));
  };

  return (
    <div className="search" ref={box}>
      <Icon.search />
      <input
        className="input"
        placeholder={searchHint}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, hits.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
          if (e.key === 'Enter') {
            e.preventDefault();
            if (hits[sel] && dq === q) { go(hits[sel]); return; }
            // A barcode scanner (like the sticker on a bought phone, B-1001) types faster than the
            // results come back and presses Enter: look it up right away and open the first match.
            const typed = q.trim();
            if (typed.length >= 2) {
              get<Hit[]>(`/api/search${qs({ q: typed })}`)
                .then((h) => { if (h[0]) go(h[0]); else { setHits([]); setOpen(true); } })
                .catch((err) => toast(err.message, 'error'));
            }
          }
          if (e.key === 'Escape') setOpen(false);
        }}
        aria-label="Search everything"
        role="combobox"
        aria-expanded={open && hits.length > 0}
        aria-controls="search-results"
      />
      {open && q.trim().length >= 2 ? (
        <div className="search-results" id="search-results" role="listbox">
          {hits.length ? (
            hits.map((h, i) => (
              <button key={`${h.type}-${h.id}`} type="button" role="option" aria-selected={i === sel} className="search-hit" onMouseEnter={() => setSel(i)} onClick={() => go(h)}>
                <span className="grow">
                  <span className="t" style={{ display: 'block' }}>{h.title}</span>
                  <span className="s" style={{ display: 'block' }}>{h.subtitle}</span>
                </span>
                {h.badge ? <span className="badge">{h.badge}</span> : null}
              </button>
            ))
          ) : (
            <div className="small muted" style={{ padding: '10px 12px' }}>No customers, phones, receipts, repairs or orders match "{q}".</div>
          )}
          {/* The suppliers' parts search runs on West Coast Wireless's own supplier accounts: its store only, for now. */}
          {can('order.create') && plan.home ? <SupplierPartHits q={q} onPick={(p) => { setOpen(false); setQ(''); startOrderWithPart(p); }} /> : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A business that subscribes: a payment that didn't go through (still open, a few days to fix
 * it) or a subscription that ended (the owner can still see and download everything).
 */
function SubscriptionBanner() {
  const plan = usePlan();
  const { me } = useSession();
  if (!plan.subscriber || plan.status === 'active' || plan.status === 'loading') return null;
  const owner = me?.employee.role_id === 'owner';
  if (plan.status === 'past_due') {
    return (
      <div className="banner warn" role="status">
        <Icon.info />
        <div className="grow"><div className="strong">The last Store OS payment didn’t go through.</div><div className="small">Everything still works for now. {owner ? 'Update the card in Billing so nothing stops.' : 'Ask the owner to update the card in Billing.'}</div></div>
        {owner ? <Button variant="primary" onClick={() => navigate('/settings/billing')}>Open Billing</Button> : null}
      </div>
    );
  }
  return (
    <div className="banner warn" role="status">
      <Icon.info />
      <div className="grow">
        <div className="strong">{plan.status === 'canceled' ? 'This business’s Store OS subscription has ended.' : 'This business is paused.'}</div>
        <div className="small">{owner ? 'You can still see and download everything (Settings → Business → Download a full copy). Restart from Billing to sell and check in again.' : 'Nothing new can be saved. Ask the owner to restart it from Billing.'}</div>
      </div>
      {owner ? <Button variant="primary" onClick={() => navigate('/settings/billing')}>Open Billing</Button> : null}
    </div>
  );
}

